/**
 * Cliente API de entes_externos (Sprint 18 P14).
 *
 * Entidades externas al barrio a las que se asignan recursos sin cobro
 * (CR citofonía portón, CR camión basura municipal, llavero cartero).
 *
 * Edición restringida al super_admin (D-P14-5).
 */
import { apiFetch } from './client';

export interface EnteExternoRead {
  id: string;
  nombre: string;
  contacto: string | null;
  notas: string | null;
  activo: boolean;
  creado_at: string;
  actualizado_at: string;
}

export interface EnteExternoCreate {
  nombre: string;
  contacto?: string | null;
  notas?: string | null;
}

export interface EnteExternoUpdate {
  nombre?: string;
  contacto?: string | null;
  notas?: string | null;
  /** `false` aplica soft-delete (D-P14-3). NO existe borrado físico. */
  activo?: boolean;
}

/** Tipos compatibles con `Recurso.tipo` que aplican a entes externos. */
export type TipoRecursoEnteExterno = 'llavero' | 'control_remoto' | 'otro';

export interface RecursoDeEnteExternoRead {
  id: string;
  lote_id: string | null;
  ente_externo_id: string | null;
  tipo: string;
  identificador: string;
  costo_reposicion: number;
  entregado_at: string;
  estado: string;
  notas: string | null;
  // Serial RFID del llavero (desde stock_recursos vía join). null para CR/
  // otro o llaveros que aún no lo tienen cargado.
  serial_rfid: string | null;
}

export interface CrearRecursoExternoDirectoIn {
  tipo: TipoRecursoEnteExterno;
  identificador: string;
  costo_reposicion: number;
  /** YYYY-MM-DD */
  entregado_at: string;
  notas?: string | null;
}

export interface AsignarStockAEnteExternoIn {
  stock_id: string;
}

export function listEntesExternos(
  filtros: { incluir_inactivos?: boolean } = {},
): Promise<EnteExternoRead[]> {
  const params = new URLSearchParams();
  if (filtros.incluir_inactivos) params.set('incluir_inactivos', 'true');
  const qs = params.toString();
  return apiFetch<EnteExternoRead[]>(
    `/entes-externos/${qs ? `?${qs}` : ''}`,
  );
}

export function getEnteExterno(id: string): Promise<EnteExternoRead> {
  return apiFetch<EnteExternoRead>(`/entes-externos/${id}`);
}

export function listRecursosDeEnteExterno(
  id: string,
): Promise<RecursoDeEnteExternoRead[]> {
  return apiFetch<RecursoDeEnteExternoRead[]>(
    `/entes-externos/${id}/recursos`,
  );
}

export function crearEnteExterno(
  data: EnteExternoCreate,
): Promise<EnteExternoRead> {
  return apiFetch<EnteExternoRead>('/entes-externos/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function actualizarEnteExterno(
  id: string,
  data: EnteExternoUpdate,
): Promise<EnteExternoRead> {
  return apiFetch<EnteExternoRead>(`/entes-externos/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/** Camino X — consume StockRecurso y crea Recurso para el ente. */
export function asignarStockAEnteExterno(
  enteId: string,
  data: AsignarStockAEnteExternoIn,
): Promise<RecursoDeEnteExternoRead> {
  return apiFetch<RecursoDeEnteExternoRead>(
    `/entes-externos/${enteId}/asignar-stock`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}

/** Camino Y — crea Recurso directo sin tocar stock. Para los 3 históricos. */
export function crearRecursoExternoDirecto(
  enteId: string,
  data: CrearRecursoExternoDirectoIn,
): Promise<RecursoDeEnteExternoRead> {
  return apiFetch<RecursoDeEnteExternoRead>(
    `/entes-externos/${enteId}/recursos`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
  );
}
